import { getManifestName } from '@wix/editor-elements-conventions';
import { createException } from '../errors/RegistryError';
import {
  ILibraryTopology,
  IRegistryManifestContext,
  IRegistryOptions,
  IResourceLoader,
  IWebpackManifestContext,
} from '../registry.types';

function chooseManifestName({
  host,
  topology,
  options,
}: {
  host: string;
  topology: ILibraryTopology;
  options: IRegistryOptions;
}) {
  if (!options.useNewManifest || !topology.namespace) {
    const manifestName = topology.manifestName
      ? topology.manifestName
      : 'manifest';

    return options.dev ? `${manifestName}.json` : `${manifestName}.min.json`;
  }

  return getManifestName({
    host,
    namespace: topology.namespace,
    isDev: options.dev || false,
  });
}

export const loadManifests = async <
  TContext extends
    | IWebpackManifestContext
    | IRegistryManifestContext = IRegistryManifestContext,
>({
  host,
  resourceLoader,
  libraries,
  options = {},
}: {
  host: string;
  resourceLoader: IResourceLoader;
  libraries: Array<ILibraryTopology>;
  options: IRegistryOptions;
}): Promise<Array<TContext>> => {
  const fetchers = libraries.map<Promise<TContext>>(async topology => {
    if (!topology.url) {
      throw createException('It is not possible to fetch library manifest')
        .withLibrary(topology.artifactId)
        .withUrl(typeof topology.url);
    }

    const manifestName = chooseManifestName({
      host,
      topology,
      options,
    });

    const url = `${topology.url}/${manifestName}`;

    const manifest = await resourceLoader.fetchLibraryManifest<
      TContext['manifest']
    >({
      url,
    });

    return {
      topology,
      manifest,
    } as TContext;
  });

  return Promise.all(fetchers);
};
